import type {
  AccountRow, ActivityEntry, AiConversation, AiMessage, AnalysisModeKey, BackupRecord,
  Bootstrap, Company, Connection, Dashboard, Dataset, FiscalPeriod, FiscalYear, Mapping,
  OrganizationSettings, QualityIssue, ReportDetail, ReportSummary, ReportTemplate, Run,
  StatementsResponse, StoredRule, Actor, AccessLevel, AccountLevel,
} from '../types';

export class ApiError extends Error {
  constructor(message: string, readonly status: number, readonly details?: unknown) {
    super(message);
    this.name = 'ApiError';
  }
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const response = await fetch(`/api/v1${path}`, {
    ...init,
    credentials: 'same-origin',
    headers: {
      Accept: 'application/json',
      ...(init.body && !(init.body instanceof FormData) ? { 'Content-Type': 'application/json' } : {}),
      ...init.headers,
    },
  });
  const contentType = response.headers.get('content-type') ?? '';
  const payload = contentType.includes('application/json') ? await response.json() : await response.text();
  if (!response.ok) {
    // The server writes its own Persian message for every typed failure; the
    // fallbacks below only cover a response that carried none at all.
    const message = payload && typeof payload === 'object' && 'error' in payload
      ? String((payload as { error: unknown }).error)
      : response.status === 401
        ? 'نشست شما پایان یافته است. دوباره وارد شوید.'
        : 'درخواست انجام نشد. کمی بعد دوباره تلاش کنید.';
    throw new ApiError(message, response.status, payload);
  }
  return payload as T;
}

const body = (value: unknown) => JSON.stringify(value);
const query = (params: Record<string, string | undefined>) => {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) if (value) search.set(key, value);
  const rendered = search.toString();
  return rendered ? `?${rendered}` : '';
};
const id = encodeURIComponent;

export const api = {
  // auth
  session: () => request<{ authenticated: boolean; mode: string; actor: Actor | null }>('/auth/session'),
  login: (username: string, password: string) =>
    request<{ actor: Actor }>('/auth/login', { method: 'POST', body: body({ username, password }) }),
  logout: () => request<{ ok: true }>('/auth/logout', { method: 'POST', body: '{}' }),
  changeOwnPassword: (currentPassword: string, newPassword: string) =>
    request<{ ok: true; message: string }>('/auth/password', { method: 'POST', body: body({ currentPassword, newPassword }) }),

  bootstrap: () => request<Bootstrap>('/bootstrap'),

  // companies + fiscal
  companies: (includeArchived = false) =>
    request<{ companies: Company[] }>(`/companies${includeArchived ? '?includeArchived=1' : ''}`),
  company: (companyId: string) => request<{
    company: Company; access: AccessLevel; counts: Company['counts'];
    fiscalYears: FiscalYear[];
    grants: Array<{ userId: string; username: string; displayName: string; role: string; level: AccessLevel }>;
  }>(`/companies/${id(companyId)}`),
  createCompany: (input: Record<string, unknown>) =>
    request<{ company: Company; fiscalYears: FiscalYear[] }>('/companies', { method: 'POST', body: body(input) }),
  updateCompany: (companyId: string, input: Record<string, unknown>) =>
    request<{ company: Company }>(`/companies/${id(companyId)}`, { method: 'PATCH', body: body(input) }),
  archiveCompany: (companyId: string, expectedVersion: number) =>
    request<{ company: Company }>(`/companies/${id(companyId)}/archive`, { method: 'POST', body: body({ expectedVersion }) }),
  setCompanyAccess: (companyId: string, userId: string, level: AccessLevel) =>
    request<{ grants: Array<{ userId: string; level: AccessLevel }> }>(`/companies/${id(companyId)}/access`, {
      method: 'PUT', body: body({ userId, level }),
    }),

  createFiscalYear: (companyId: string, startIso: string) =>
    request<{ fiscalYear: FiscalYear }>(`/companies/${id(companyId)}/fiscal-years`, { method: 'POST', body: body({ startIso }) }),
  setFiscalYearStatus: (companyId: string, fiscalYearId: string, status: 'open' | 'closed') =>
    request<{ fiscalYear: FiscalYear }>(`/companies/${id(companyId)}/fiscal-years/${id(fiscalYearId)}`, {
      method: 'PATCH', body: body({ status }),
    }),
  periods: (companyId: string, fiscalYearId: string) =>
    request<{ periods: FiscalPeriod[] }>(`/companies/${id(companyId)}/fiscal-years/${id(fiscalYearId)}/periods`),

  // pipeline
  startRun: (
    companyId: string, files: File[], fiscalYearId: string, periodKey: string,
    accountLevel: AccountLevel,
  ) => {
    const data = new FormData();
    data.append('fiscalYearId', fiscalYearId);
    data.append('periodKey', periodKey);
    data.append('accountLevel', accountLevel);
    for (const file of files) data.append('files', file, file.name);
    return request<{ run: Run }>(`/companies/${id(companyId)}/runs`, { method: 'POST', body: data });
  },
  runs: (companyId: string) => request<{ runs: Run[] }>(`/companies/${id(companyId)}/runs`),
  run: (companyId: string, runId: string) => request<{ run: Run }>(`/companies/${id(companyId)}/runs/${id(runId)}`),
  resumeRun: (companyId: string, runId: string) =>
    request<{ run: Run }>(`/companies/${id(companyId)}/runs/${id(runId)}/execute`, { method: 'POST', body: '{}' }),
  correctEntry: (
    companyId: string, runId: string, entryId: string,
    input: {
      action: 'edit' | 'exclude';
      patch?: Partial<{
        entryDateIso: string | null; documentNumber: string; description: string;
        accountCode: string; accountName: string; debit: number; credit: number;
      }>;
    },
  ) => request<{ run: Run }>(
    `/companies/${id(companyId)}/runs/${id(runId)}/entries/${id(entryId)}`,
    { method: 'PATCH', body: body(input) },
  ),

  datasets: (companyId: string, runId?: string) =>
    request<{ datasets: Dataset[]; skippedFiles: string[] }>(`/companies/${id(companyId)}/datasets${query({ runId })}`),
  mappings: (companyId: string, runId?: string) =>
    request<{ mappings: Mapping[] }>(`/companies/${id(companyId)}/mappings${query({ runId })}`),
  decideMapping: (companyId: string, mappingId: string, input: { decision: 'approved' | 'rejected' | 'reset'; expectedVersion: number; fields?: Record<string, number> }) =>
    request<{ ok: true }>(`/companies/${id(companyId)}/mappings/${id(mappingId)}`, { method: 'POST', body: body(input) }),

  accounts: (companyId: string) => request<{ accounts: AccountRow[] }>(`/companies/${id(companyId)}/accounts`),
  reclassifyAccount: (companyId: string, code: string, category: string) =>
    request<{ ok: true }>(`/companies/${id(companyId)}/accounts/${id(code)}`, { method: 'PATCH', body: body({ category }) }),

  qualityExplanation: (companyId: string, fiscalYearId?: string, periodKey?: string) =>
    request<{ noteFa: string; model: string | null }>(
      `/companies/${id(companyId)}/quality/explanation${query({ fiscalYearId, periodKey })}`,
    ),

  quality: (companyId: string, runId?: string) =>
    request<{ runId: string; issues: QualityIssue[] }>(`/companies/${id(companyId)}/quality${query({ runId })}`),
  resolveIssue: (companyId: string, issueId: string, status: 'open' | 'resolved' | 'ignored') =>
    request<{ ok: true }>(`/companies/${id(companyId)}/quality/${id(issueId)}`, { method: 'POST', body: body({ status }) }),

  // analysis
  dashboard: (companyId: string, fiscalYearId: string, periodKey: string) =>
    request<Dashboard>(`/companies/${id(companyId)}/dashboard${query({ fiscalYearId, periodKey })}`),
  statements: (companyId: string, fiscalYearId: string, periodKey: string) =>
    request<StatementsResponse>(`/companies/${id(companyId)}/statements${query({ fiscalYearId, periodKey })}`),

  // ai
  conversations: (
    companyId: string,
    filters: { fiscalYearId?: string; periodKey?: string; mode?: AnalysisModeKey } = {},
  ) => request<{ conversations: AiConversation[] }>(
    `/companies/${id(companyId)}/ai/conversations${query(filters)}`,
  ),
  messages: (companyId: string, conversationId: string) =>
    request<{ messages: AiMessage[] }>(`/companies/${id(companyId)}/ai/conversations/${id(conversationId)}`),
  // `signal` is what makes the composer's stop button real: the answer is one
  // request, so stopping it means abandoning that request rather than asking
  // the server to interrupt a stream it is not sending.
  ask: (
    companyId: string,
    input: { question: string; mode: AnalysisModeKey; fiscalYearId: string; periodKey: string; conversationId?: string },
    signal?: AbortSignal,
  ) =>
    request<{ conversationId: string; answer: string; model: string; unverified: string[] }>(
      `/companies/${id(companyId)}/ai/ask`, { method: 'POST', body: body(input), signal },
    ),
  integrity: (companyId: string, fiscalYearId: string) =>
    request<{ reliable: boolean; findings: QualityIssue[]; noteFa: string; model: string | null }>(
      `/companies/${id(companyId)}/ai/integrity`, { method: 'POST', body: body({ fiscalYearId }) },
    ),

  // reports
  reports: (companyId: string, filters: { fiscalYearId?: string; kind?: string; status?: string } = {}) =>
    request<{ kinds: Array<{ key: string; labelFa: string; descriptionFa: string; requiresApproval: boolean }>; reports: ReportSummary[] }>(
      `/companies/${id(companyId)}/reports${query(filters)}`,
    ),
  generateReport: (companyId: string, input: { kind: string; fiscalYearId: string; periodKey: string; detailLevel: string; narrate: boolean }) =>
    request<{ report: ReportDetail & { noteFa: string } }>(`/companies/${id(companyId)}/reports`, { method: 'POST', body: body(input) }),
  report: (companyId: string, reportId: string) =>
    request<{ report: ReportDetail }>(`/companies/${id(companyId)}/reports/${id(reportId)}`),
  decideReport: (companyId: string, reportId: string, decision: 'approved' | 'rejected') =>
    request<{ report: ReportSummary }>(`/companies/${id(companyId)}/reports/${id(reportId)}/decision`, {
      method: 'POST', body: body({ decision }),
    }),
  reportExportUrl: (companyId: string, reportId: string, format: 'html' | 'md' | 'pdf' | 'xlsx') =>
    `/api/v1/companies/${id(companyId)}/reports/${id(reportId)}/export?format=${format}`,

  /** The full set, presented per استاندارد حسابداری شماره ۱ (تجدیدنظرشده ۱۳۹۷). */
  statementsExportUrl: (
    companyId: string, fiscalYearId: string, periodKey: string,
    format: 'html' | 'xlsx' | 'pdf', currency: 'toman' | 'rial',
  ) => `/api/v1/companies/${id(companyId)}/statements/export`
    + `?fiscalYearId=${id(fiscalYearId)}&periodKey=${encodeURIComponent(periodKey)}`
    + `&format=${format}&currency=${currency}`,

  // administration
  settings: () => request<{ settings: OrganizationSettings; version: number }>('/settings'),
  updateSettings: (input: Partial<OrganizationSettings> & { expectedVersion: number }) =>
    request<{ settings: OrganizationSettings; version: number }>('/settings', { method: 'PATCH', body: body(input) }),

  users: () => request<{ users: Actor[]; roles: Array<{ key: string; labelFa: string; permissions: string[] }> }>('/users'),
  createUser: (input: Record<string, unknown>) => request<{ user: Actor }>('/users', { method: 'POST', body: body(input) }),
  updateUser: (userId: string, input: Record<string, unknown>) =>
    request<{ user: Actor }>(`/users/${id(userId)}`, { method: 'PATCH', body: body(input) }),
  resetUserPassword: (userId: string, password: string) =>
    request<{ ok: true }>(`/users/${id(userId)}/password`, { method: 'POST', body: body({ password }) }),

  rules: (filters: { kind?: string; companyId?: string } = {}) =>
    request<{ rules: StoredRule[] }>(`/rules${query(filters)}`),
  saveRule: (input: Record<string, unknown>) => request<{ rule: StoredRule }>('/rules', { method: 'POST', body: body(input) }),
  deleteRule: (ruleId: string) => request<{ ok: true }>(`/rules/${id(ruleId)}`, { method: 'DELETE' }),

  connections: () => request<{ connections: Connection[] }>('/connections'),
  templates: () => request<{ templates: ReportTemplate[] }>('/templates'),
  saveTemplate: (input: Record<string, unknown>) =>
    request<{ template: { id: string; kind: string } }>('/templates', { method: 'POST', body: body(input) }),

  backups: () => request<{ backups: BackupRecord[]; disk: { count: number; bytes: number } }>('/backups'),
  createBackup: () => request<{ backup: BackupRecord }>('/backups', { method: 'POST', body: '{}' }),
  deleteBackup: (backupId: string) => request<{ ok: true }>(`/backups/${id(backupId)}`, { method: 'DELETE' }),
  backupDownloadUrl: (backupId: string) => `/api/v1/backups/${id(backupId)}/download`,

  activity: (limit = 100) => request<{ activity: ActivityEntry[] }>(`/activity?limit=${limit}`),
};
